import fontData from '@/assets/typefaces/Droid Sans_Bold.json';
import { ActionManager, Animation, Camera, Color3, Engine, ExecuteCodeAction, HighlightLayer, Material, Mesh, MeshBuilder, Scene, SceneOptions, SetValueAction, StandardMaterial, Texture, TransformNode, Vector3 } from '@babylonjs/core';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D';
import { ITextRenderer, RendererHelper } from 'Api/Contracts/Dtos';
import { createAnimation } from 'App/Helpers/Animations/AnimationHelper';
import earcut from 'earcut';
import { IHotspot, IIconProvider, ITooltipStyle, TextureTypes, TooltipStyle } from 'Framework/Components/Controls/XView/Core';
import { Tooltip } from 'Framework/Components/Controls/XView/GUI';
import { CreatePolygon3D } from 'Framework/Components/Controls/XView/Meshes';
import { ResizedTextureProvider } from 'Framework/Components/Controls/XView/XView.Textures';
import { Viewer360 } from 'Framework/Components/Controls/XView/XView.Viewer360';
import { ColorHelper } from 'Framework/Helpers/ColorHelper';
import { EnumHelper } from 'Framework/Helpers/EnumHelper';

export abstract class BaseHotspotOverlay extends Scene {
    public constructor(engine: Engine, camera: Camera, viewer: Viewer360, iconProvider: IIconProvider, options?: SceneOptions) {
        super(engine, options);
        this.addTransformNode(new TransformNode('root'));

        this._viewer = viewer;
        this._iconProvider = iconProvider;

        this._textureProvider = new ResizedTextureProvider(64, 64);
        this._tooltipsGui = AdvancedDynamicTexture.CreateFullscreenUI('tooltipsGUI', true, this);
        this._highlightLayers = {};

        this.activeCamera = camera;
        this.autoClear = false;

        this.onBeforeRenderObservable.add(e => {
            let fov = this.activeCamera.fov;
            let scaleFactor = (80 / 180 * Math.PI / fov);

            this._tooltipsGui.renderScale = 1 / scaleFactor;
        });
    }

    public clear(): void {
        while (this.meshes.length > 0) {
            const mesh = this.meshes[0];
            mesh.dispose(false, true);
        }

        this._tooltipsGui.executeOnAllControls(tooltip => {
            tooltip.dispose();
        });

        for (const key in this._highlightLayers) {
            this._highlightLayers[key].dispose();
            delete this._highlightLayers[key];
        }
    }

    public async loadHotspotsAsync(hotspots: Array<IHotspot>): Promise<void> {
        for (const hotspot of hotspots) {
            const isIconHotspot = hotspot.coordinates.length == 1;

            try {
                const mesh: Mesh = await this._createHotspotMeshAsync(hotspot);

                this._addEventListeners(hotspot, mesh);

                this._addTooltip(hotspot, mesh, isIconHotspot);
            }
            catch {
                console.error(`Error loading hotspot: ${hotspot.name}`);
                continue;
            }
        }
    }

    public setOrigin(originX: number, originY: number, originZ: number): void {
        const transformNode = this.getTransformNodeByName('root');

        transformNode.position = new Vector3(originX, originY, originZ);
    }

    private _addTooltip(hotspot: IHotspot<any>, mesh: Mesh, isIconHotspot: boolean) {
        if (hotspot.content.tooltip) {
            const tooltipStyle: ITooltipStyle = hotspot.content.tooltipStyle ?? (isIconHotspot
                ? TooltipStyle.defaultTop
                : TooltipStyle.defaultCenter);

            const _ = new Tooltip(
                hotspot.content.tooltip,
                this._tooltipsGui,
                mesh,
                mesh.actionManager,
                tooltipStyle
            );
        }
    }

    private _addEventListeners(hotspot: IHotspot<any>, mesh: Mesh): void {
        if (hotspot.onclick) {
            mesh.actionManager.registerAction(new ExecuteCodeAction(
                ActionManager.OnPickTrigger,
                _ => {
                    hotspot.onclick(hotspot);
                }
            ));
        }
    }

    private async _createHotspotMeshAsync(hotspot: IHotspot): Promise<Mesh> {
        let mesh: Mesh;

        if (RendererHelper.isIconRenderer(hotspot.content)) {
            mesh = await this._createIconMeshAsync(hotspot);
        }
        else if (RendererHelper.isShapeRenderer(hotspot.content)) {
            mesh = await this._createShapeMeshAsync(hotspot);
        }
        else if (RendererHelper.isTextRenderer(hotspot.content)) {
            mesh = await this._createTextMeshAsync(hotspot);
        }
        else {
            throw new Error('Not supported');
        }

        mesh.parent = this.getTransformNodeByName('root');

        return mesh;
    }

    private async _createTextMeshAsync(hotspot: IHotspot): Promise<Mesh> {
        const content = hotspot.content as ITextRenderer;
        const doubleMargin = content.size / 4;

        const textMesh = MeshBuilder.CreateText(
            'text',
            content.text,
            fontData,
            { size: content.size, depth: Number.MIN_VALUE },
            this,
            earcut);

        const boundingBox = textMesh.getBoundingInfo().boundingBox;
        const size = boundingBox.maximum.subtract(boundingBox.minimum);

        //Center mesh origin
        textMesh.position = size.scale(0.5).subtract(boundingBox.maximum);
        textMesh.bakeCurrentTransformIntoVertices();

        const material = new StandardMaterial('textMaterial', this);

        const rgbaColor = ColorHelper.ParseColorToRgba(content.color);
        material.emissiveColor = Color3.FromInts(rgbaColor.r, rgbaColor.g, rgbaColor.b);
        material.alpha = rgbaColor.a;

        textMesh.material = material;

        const box = MeshBuilder.CreateBox(
            'box',
            {
                width: size.x + doubleMargin,
                height: size.y + doubleMargin,
                depth: size.z
            },
            this);

        box.actionManager = new ActionManager(this);

        box.material = new StandardMaterial('transparent', this);
        box.material.alpha = 0;

        textMesh.parent = box;

        await this.onMeshIconCreatedAsync(hotspot, box);

        return box;
    }

    private async _createIconMeshAsync(hotspot: IHotspot): Promise<Mesh> {
        let mesh = MeshBuilder.CreatePlane(hotspot.name, { size: hotspot.content.size }, this);
        const material = new StandardMaterial(`${hotspot.name}-mat`, this);

        const iconInfos = this._iconProvider.getIconInfo(hotspot);

        let texture: Texture;

        if (iconInfos.animation) {
            texture = await this._textureProvider.createAnimatedSpriteTextureAsync(
                `${hotspot.name}-tex`,
                iconInfos.url,
                this,
                iconInfos.animation.nRows,
                iconInfos.animation.nCols,
                iconInfos.animation.frameRate
            );

            this.beginAnimation(texture, iconInfos.animation.startFrame, iconInfos.animation.endFrame + 1, true);
        }
        else {
            texture = await this._textureProvider.createIconTextureAsync(
                `${hotspot.content.icon}-tex`,
                iconInfos.url,
                this
            );
        }

        if (EnumHelper.hasFlag(iconInfos.textureType, TextureTypes.OpacityTexture)) {
            texture.hasAlpha = true;
            material.opacityTexture = texture;

            if (iconInfos.textureType == TextureTypes.OpacityTexture) {
                const rgbaColor = ColorHelper.ParseColorToRgba(hotspot.content.color);
                material.emissiveColor = Color3.FromInts(rgbaColor.r, rgbaColor.g, rgbaColor.b);
                material.alpha = rgbaColor.a;
            }
        }

        if (EnumHelper.hasFlag(iconInfos.textureType, TextureTypes.EmissiveTexture)) {
            material.emissiveTexture = texture;
        }

        if (EnumHelper.hasFlag(iconInfos.textureType, TextureTypes.AmbientTexture)) {
            material.ambientTexture = texture;
        }

        if (EnumHelper.hasFlag(iconInfos.textureType, TextureTypes.SpecularTexture)) {
            material.specularTexture = texture;
        }

        if (EnumHelper.hasFlag(iconInfos.textureType, TextureTypes.DiffuseTexture)) {
            material.diffuseTexture = texture;
        }

        mesh.material = material;

        if (hotspot.content.animation) {
            mesh.material.backFaceCulling = false;

            //Create a wrapper around the icon to ensure that the size is fixed
            //and does not vary depending on the animation otherwise the action is no longer possible.
            const iconContainer: Mesh = MeshBuilder.CreatePlane(
                'iconContainer',
                {
                    size: hotspot.content.size
                },
                this);

            this._transparentMaterial ??= this._createTransparentMaterial();
            iconContainer.material = this._transparentMaterial;

            iconContainer.isPickable = true;
            iconContainer.actionManager = new ActionManager(this);

            iconContainer.addChild(mesh);

            const configuredAnimation: Array<Animation> = createAnimation(hotspot.content.animation);

            mesh.animations.push(...configuredAnimation);

            this.beginAnimation(
                mesh,
                0,
                hotspot.content.animation.frameRate,
                true,
                hotspot.content.animation.animationSpeed);

            mesh = iconContainer;
        }

        mesh.actionManager = new ActionManager(this);

        await this.onMeshIconCreatedAsync(hotspot, mesh);

        return mesh;
    }

    protected abstract onMeshIconCreatedAsync(hotspot: IHotspot, mesh: Mesh): Promise<void>;
    protected abstract getCartesianCoordinatesOfShapeAsync(hotspot: IHotspot): Promise<Array<Vector3>>;

    private async _createShapeMeshAsync(hotspot: IHotspot): Promise<Mesh> {
        const cartesianCoordinates = await this.getCartesianCoordinatesOfShapeAsync(hotspot);
        const rgbaColor = ColorHelper.ParseColorToRgba(hotspot.content.color);
        const rgbaHoverColor = ColorHelper.ParseColorToRgba(hotspot.content.hoverColor);

        const polygon = this._createPolygon(
            cartesianCoordinates,
            Color3.FromInts(rgbaColor.r, rgbaColor.g, rgbaColor.b),
            rgbaColor.a,
            Color3.FromInts(rgbaHoverColor.r, rgbaHoverColor.g, rgbaHoverColor.b),
            rgbaHoverColor.a
        );

        if (hotspot.content.stroke.width > 0) {
            const rgbaStrokeColor = ColorHelper.ParseColorToRgba(hotspot.content.stroke.color);
            const strokeColor = Color3.FromInts(rgbaStrokeColor.r, rgbaStrokeColor.g, rgbaStrokeColor.b);
            this._createOutline(polygon, hotspot.content.stroke.width, strokeColor);
        }

        return polygon;
    }

    private _createPolygon(coordinates: Array<Vector3>, color: Color3, alpha: number, hoverColor: Color3, hoverAlpha: number): Mesh {
        const polygon = CreatePolygon3D('polygon', coordinates, this);

        const material = new StandardMaterial('material', this);
        material.emissiveColor = color;
        material.alpha = alpha;
        material.backFaceCulling = false;

        polygon.material = material;
        polygon.freezeWorldMatrix();

        polygon.actionManager = new ActionManager(this);

        polygon.actionManager.registerAction(new SetValueAction(
            ActionManager.OnPointerOverTrigger,
            material,
            'emissiveColor',
            hoverColor
        ));

        polygon.actionManager.registerAction(new SetValueAction(
            ActionManager.OnPointerOverTrigger,
            material,
            'alpha',
            hoverAlpha
        ));

        polygon.actionManager.registerAction(new SetValueAction(
            ActionManager.OnPointerOutTrigger,
            material,
            'emissiveColor',
            color
        ));

        polygon.actionManager.registerAction(new SetValueAction(
            ActionManager.OnPointerOutTrigger,
            material,
            'alpha',
            alpha
        ));

        return polygon;
    }

    private _createOutline(mesh: Mesh, width: number, color: Color3): void {
        let highlightLayer = this._highlightLayers[width];

        if (!highlightLayer) {
            highlightLayer = new HighlightLayer(
                'outline',
                this,
                {
                    isStroke: true,
                    //Empirical formula to get a seemingly linear grow from a min size of 1
                    //to a max size of 15, with a decent resolution.
                    //An alternative is to use constant mainTextureFixedSize and play with blurVerticalSize
                    //and blurHorizontalSize, but it causes aliasing in the angles
                    mainTextureFixedSize: 8192 / (width + 1) - 300
                }
            );

            this._highlightLayers[width] = highlightLayer;
        }

        highlightLayer.addMesh(mesh, color);
    }

    private _createTransparentMaterial(): Material {
        const transparentMaterial: Material = new StandardMaterial('transparentMat', this);
        transparentMaterial.alpha = 0;

        return transparentMaterial;
    }

    private _transparentMaterial: Material;
    protected readonly _textureProvider: ResizedTextureProvider;
    protected readonly _viewer: Viewer360;
    protected readonly _iconProvider: IIconProvider;
    protected readonly _tooltipsGui: AdvancedDynamicTexture;
    protected readonly _highlightLayers: Record<number, HighlightLayer>;
}
